import { BorderBox, FlexBox, Text } from '@trmediaab/zebra';
import PropTypes from 'prop-types';
import { useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import RoundHeader from '~/components/RoundNavigation/RoundHeader';
import { ThemedLink } from '~/components/ThemedLink';
import { selectPremium } from '~/containers/Auth/selectors';
import { selectContent } from '~/containers/Content/selectors';
import { loadLiveFeed } from '~/containers/LiveFeed/actions';
import { selectCountUnread } from '~/containers/LiveFeed/selectors';

import { BAR_HEIGHT } from './constants';
import { shouldShowInterviewsCount } from './utils';

const UNDERLINE_STYLE = {
  position: 'absolute',
  content: '""',
  bottom: '12px',
  display: 'block',
  width: '100%',
  height: '3px',
  backgroundColor: 'brands.travrondenSpel',
};

const LIVE_ENABLED_ROUNDS = new Set(['V75', 'V86']);

const Count = ({ count, betType }) => {
  if (!count) {
    return null;
  }

  return (
    <BorderBox
      display="flex"
      alignItems="center"
      borderRadius="40px"
      ml="1,5"
      height="24px"
      padding="5px 9px 6px 9px"
      backgroundColor={`atg.${betType}.primary`}
    >
      <Text color="white">{count}</Text>
    </BorderBox>
  );
};

const RoundNavigation = ({
  round,
  slug,
  view,
  unread_liveitems,
  variant = 'round',
  loadLiveFeed,
  premiumAccess,
  content,
  location,
}) => {
  const isRoundView = variant === 'round';

  useEffect(() => {
    if (
      !isRoundView &&
      LIVE_ENABLED_ROUNDS.has(round.bet_type) &&
      premiumAccess
    ) {
      loadLiveFeed(slug);
    }
  }, [isRoundView, slug, loadLiveFeed, round.bet_type, premiumAccess]);

  const links = useMemo(() => {
    const linksArray = [];

    if (isRoundView) {
      linksArray.push({
        label: 'ÖVERSIKT',
        payload: {
          slug,
          bet_type: round.bet_type.toLowerCase(),
        },
        isDefault: true,
      });
    }

    linksArray.push(
      {
        label: 'STARTLISTOR & TIPS',
        payload: { view: 'startlista', leg_number: 1 },
      },
      {
        label: 'INTERVJUER',
        count: shouldShowInterviewsCount(round) && (
          <Count count={round.interview_count} betType={round.bet_type} />
        ),
        payload: { view: 'intervjuer', leg_number: 1 },
      },
      {
        label: 'VÅRA SPEL',
        count: <Count count={round.system_count} betType={round.bet_type} />,
        payload: { view: 'vara-spel' },
      },
    );

    if (LIVE_ENABLED_ROUNDS.has(round.bet_type)) {
      linksArray.push({
        label: 'LIVE',
        count: (
          <Count
            count={!premiumAccess ? round.live_count : unread_liveitems}
            betType={round.bet_type}
          />
        ),
        payload: { view: 'live' },
      });
    }

    if (round.meets[0].country === 'SE' && isRoundView) {
      linksArray.push({
        label: 'STATISTIK',
        payload: { view: 'statistik' },
      });
    }

    if (content && isRoundView) {
      const articleTypes = [
        ...new Set(content.results.map(article => article.type)),
      ];
      const typeToLink = {
        news_article: {
          label: 'NYHETER',
          type: 'ROUND_NEWS_LIST',
          childType: 'ROUND_NEWS',
          payload: { slug: round.slug },
        },
        chronicle: {
          label: 'KRÖNIKOR',
          type: 'ROUND_CHRONICLE_LIST',
          childType: 'ROUND_CHRONICLE',
          payload: { slug: round.slug },
        },
        tv: {
          label: 'TV',
          type: 'ROUND_TV_LIST',
          childType: 'ROUND_TV',
          payload: { slug: round.slug },
        },
      };

      articleTypes.forEach(type => {
        const link = typeToLink[type];
        if (link) {
          linksArray.push(link);
        }
      });
    }

    return linksArray;
  }, [isRoundView, slug, round, premiumAccess, unread_liveitems, content]);

  return (
    <>
      <ThemedLink
        to={{
          type: 'ROUND_TIP',
          payload: { slug, bet_type: round.bet_type.toLowerCase() },
        }}
      >
        <RoundHeader round={round} />
      </ThemedLink>

      <FlexBox
        alignItems="center"
        height={BAR_HEIGHT}
        backgroundColor="white"
        overflowX="auto"
        gap="3"
        px="3"
        sx={{
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        {links
          .filter(Boolean)
          .map(({ payload, label, count, type, childType, isDefault }) => {
            const isActive = isDefault
              ? !view && location.type === 'ROUND_TIP'
              : (payload.view !== undefined && payload.view === view) ||
                (payload.view === 'startlista' &&
                  (view === 'tips' || view === 'loppstatistik')) ||
                type === location.type ||
                childType === location.type;
            return (
              <ThemedLink
                key={label}
                to={{
                  type: type ?? 'ROUND_TIP',
                  payload: {
                    slug,
                    ...payload,
                    bet_type: round.bet_type.toLowerCase(),
                  },
                }}
                display="flex"
                alignItems="center"
                height={BAR_HEIGHT}
                fontSize="1"
                fontWeight="bold"
                minWidth="fit-content"
                color="brands.travrondenSpel"
                sx={{
                  '&:hover': { color: 'brands.travrondenSpel' },
                }}
              >
                <Text
                  display="flex"
                  alignItems="center"
                  height={BAR_HEIGHT}
                  sx={{
                    position: 'relative',
                    '&:hover:after': UNDERLINE_STYLE,
                    ...(isActive && { '&:after': UNDERLINE_STYLE }),
                  }}
                >
                  {label}
                </Text>
                {count}
              </ThemedLink>
            );
          })}
      </FlexBox>
    </>
  );
};

RoundNavigation.propTypes = {
  round: PropTypes.object.isRequired,
  slug: PropTypes.string.isRequired,
  loadLiveFeed: PropTypes.func.isRequired,
  unread_liveitems: PropTypes.number.isRequired,
  content: PropTypes.object,
  view: PropTypes.string,
  premiumAccess: PropTypes.bool,
  variant: PropTypes.oneOf(['round', 'feed']),
  location: PropTypes.object,
};

const mapDispatchToProps = dispatch => ({
  loadLiveFeed: slug => dispatch(loadLiveFeed(slug)),
});

const mapStateToProps = createStructuredSelector({
  unread_liveitems: selectCountUnread,
  premiumAccess: selectPremium,
  content: selectContent,
  location: state => state.location,
});

export default connect(mapStateToProps, mapDispatchToProps)(RoundNavigation);
